import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Chip,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { t, lang } from '../i18n';

// A titled, filterable, single-select list used by the Browse page panels.
// `options` is an array of { value, label } objects: `value` is the kanji name
// sent back on select (an empty string "" is a real selectable level — rendered
// with `emptyLabel`), `label` is the romaji shown to English users. `selected`
// is the current value (kanji) or null.
const FilterableList = ({
  title,
  subtitle,
  options,
  selected,
  onSelect,
  emptyLabel,
  loading = false,
}) => {
  const [filter, setFilter] = useState('');

  // Text shown for an option, in the browser's language (kanji value or romaji
  // label, falling back to the value when a label is missing).
  const optionText = (o) => (lang === 'ja' ? o.value : o.label || o.value);

  const visible = useMemo(() => {
    const list = [...options].sort((a, b) =>
      optionText(a).localeCompare(optionText(b), lang === 'ja' ? 'ja' : 'en')
    );
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    // Match any script — kanji value, romaji label, or katakana reading.
    return list.filter(
      (o) =>
        (o.value || '').toLowerCase().includes(q) ||
        (o.label || '').toLowerCase().includes(q) ||
        (o.katakana || '').toLowerCase().includes(q)
    );
  }, [options, filter]);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {title}
        {subtitle && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {subtitle}
          </Typography>
        )}
      </Typography>

      <TextField
        fullWidth
        size="small"
        color="secondary"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder={t('filter…', '絞り込み…')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />

      <List
        dense
        sx={{
          maxHeight: 220,
          overflow: 'auto',
          bgcolor: 'background.default',
          borderRadius: 1,
        }}
      >
        {loading && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {t('Loading…', '読み込み中…')}
          </Typography>
        )}
        {!loading && visible.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {t('No matches', '該当なし')}
          </Typography>
        )}
        {!loading &&
          visible.map((opt) => {
            const isSelected = opt.value === selected;
            const isEmpty = opt.value === '';
            return (
              <ListItemButton
                key={opt.value || '__empty__'}
                selected={isSelected}
                onClick={() => onSelect(opt.value)}
              >
                <ListItemText
                  primary={isEmpty ? emptyLabel || t('(none)', '（なし）') : optionText(opt)}
                  primaryTypographyProps={{
                    fontStyle: isEmpty ? 'italic' : 'normal',
                    color: isEmpty ? 'text.secondary' : 'text.primary',
                  }}
                />
                {isSelected && (
                  <Chip label={t('selected', '選択中')} size="small" color="secondary" />
                )}
              </ListItemButton>
            );
          })}
      </List>
    </Box>
  );
};

export default FilterableList;
